import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AuthImg, UploadButton } from "../components/AuthImg";
import { DocumentTile } from "../components/DocumentViewer";
import { toast } from "sonner";
import { ArrowLeft, Truck, Edit3, Link2, History, FileText, Image as ImageIcon, X, Save } from "lucide-react";
import { TrailerFormDialog } from "./Remorci";

const DOC_KINDS = [
  { code: "carte_tehnica", label: "Carte tehnică (Fahrzeugausweis)" },
  { code: "asigurare", label: "Asigurare" },
  { code: "itp", label: "ITP / MFK" },
  { code: "altele", label: "Alte documente" },
];

export default function RemorcaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["admin", "planificator", "magazioner"].includes(user?.role);

  const [trailer, setTrailer] = useState(null);
  const [cars, setCars] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [linkDialog, setLinkDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, h] = await Promise.all([
        api.get(`/trailers/${id}`),
        api.get("/cars").catch(() => ({ data: [] })),
        api.get(`/trailers/${id}/history`).catch(() => ({ data: [] })),
      ]);
      setTrailer(t.data);
      setCars(c.data || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remorcă inexistentă");
      navigate("/remorci");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const carById = useMemo(() => Object.fromEntries(cars.map((c) => [c.id, c])), [cars]);
  const linkedCar = trailer?.linked_car_id ? carById[trailer.linked_car_id] : null;

  const onUploadDoc = async (fileRec, kind) => {
    try {
      const docs = Array.isArray(trailer.documente) ? [...trailer.documente] : [];
      docs.push({
        file_id: fileRec.id,
        name: fileRec.original_filename || "document",
        kind,
        uploaded_at: new Date().toISOString(),
        uploaded_by: user?.name || user?.username,
      });
      await api.put(`/trailers/${id}`, { documente: docs });
      toast.success("Document încărcat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la upload");
    }
  };

  const onUploadPhoto = async (fileRec) => {
    try {
      const photos = Array.isArray(trailer.photo_ids) ? [...trailer.photo_ids] : [];
      photos.push(fileRec.id);
      await api.put(`/trailers/${id}`, { photo_ids: photos });
      toast.success("Poză încărcată");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la upload");
    }
  };

  const deleteDoc = async (fileId) => {
    const docs = (trailer.documente || []).filter((d) => d.file_id !== fileId);
    await api.put(`/trailers/${id}`, { documente: docs });
    toast.success("Document șters");
    load();
  };

  const deletePhoto = async (fileId) => {
    const photos = (trailer.photo_ids || []).filter((p) => p !== fileId);
    await api.put(`/trailers/${id}`, { photo_ids: photos });
    toast.success("Poză ștearsă");
    load();
  };

  if (loading || !trailer) return <div className="card-flat p-8 text-sm text-zinc-400">Se încarcă…</div>;

  return (
    <div className="space-y-4" data-testid="trailer-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/remorci")}>
            <ArrowLeft size={16} className="mr-1"/> Înapoi
          </Button>
          <div className="w-11 h-11 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Truck className="text-amber-700" size={22}/>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-extrabold">
                {trailer.numar_inmatriculare || trailer.marca || "Remorcă"}
              </h1>
              {trailer.intern_number && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-bold">#{trailer.intern_number}</span>
              )}
              {trailer.active === false && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">INACTIVĂ</span>
              )}
            </div>
            <div className="text-xs text-zinc-500">
              {[trailer.marca, trailer.model, trailer.an_fabricatie].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setLinkDialog(true)} data-testid="trailer-link-btn">
              <Link2 size={14} className="mr-1"/> {trailer.linked_car_id ? "Schimbă autoturism" : "Atașează"}
            </Button>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="trailer-edit-btn">
              <Edit3 size={14} className="mr-1"/> Editează
            </Button>
          </div>
        )}
      </div>

      {linkedCar && (
        <div className="card-flat p-3 bg-emerald-50 border-emerald-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Link2 size={16} className="text-emerald-700"/>
            <div className="text-sm">
              Atașată la autoturismul <span className="font-bold">{linkedCar.numar_inmatriculare || linkedCar.marca}</span>
              {linkedCar.marca && linkedCar.numar_inmatriculare && <span className="text-zinc-500"> · {linkedCar.marca} {linkedCar.model || ""}</span>}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/autoturisme/${linkedCar.id}`)} className="text-emerald-700 hover:bg-emerald-100">
            Deschide autoturismul →
          </Button>
        </div>
      )}

      <Tabs defaultValue="info">
        <TabsList className="flex-wrap">
          <TabsTrigger value="info" data-testid="tab-info">Informații</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs"><FileText size={13} className="mr-1"/>Documente ({(trailer.documente || []).length})</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon size={13} className="mr-1"/>Poze ({(trailer.photo_ids || []).length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History size={13} className="mr-1"/>Istoric ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="card-flat p-5 space-y-3">
          <Grid label="Nr. înmatriculare" value={trailer.numar_inmatriculare}/>
          <Grid label="Nr. intern" value={trailer.intern_number}/>
          <Grid label="Marca" value={trailer.marca}/>
          <Grid label="Model" value={trailer.model}/>
          <Grid label="An fabricație" value={trailer.an_fabricatie}/>
          <Grid label="VIN / Șasiu" value={trailer.vin} mono/>
          <Grid label="Culoare" value={trailer.culoare}/>
          <Grid label="Capacitate (kg)" value={trailer.capacitate_kg}/>
          <Grid label="Status" value={trailer.active === false ? "Inactivă" : "Activă"}/>
          {trailer.notes && (
            <div className="pt-2 border-t border-[var(--border)]">
              <Label className="small-label">Note</Label>
              <div className="text-sm text-zinc-700 whitespace-pre-wrap mt-1">{trailer.notes}</div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="docs" className="card-flat p-5 space-y-4">
          {canManage && (
            <div className="flex items-center gap-2 flex-wrap">
              {DOC_KINDS.map((k) => (
                <UploadButton
                  key={k.code}
                  label={`+ ${k.label}`}
                  accept="application/pdf,image/*"
                  onUploaded={(f) => onUploadDoc(f, k.code)}
                  testId={`upload-doc-${k.code}`}
                />
              ))}
            </div>
          )}
          {(trailer.documente || []).length === 0 ? (
            <div className="text-sm text-zinc-400 italic text-center py-6">Niciun document încărcat.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(trailer.documente || []).map((d) => (
                <div key={d.file_id} className="card-flat p-3 relative group" data-testid={`trailer-doc-${d.file_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                      {DOC_KINDS.find((k) => k.code === d.kind)?.label || "Document"}
                    </div>
                    {canManage && (
                      <button
                        onClick={() => deleteDoc(d.file_id)}
                        className="p-1 rounded text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Șterge"
                      >
                        <X size={13}/>
                      </button>
                    )}
                  </div>
                  <DocumentTile fileId={d.file_id} name={d.name} />
                  {d.uploaded_at && (
                    <div className="text-[10px] text-zinc-400 mt-1.5">
                      {(d.uploaded_at || "").slice(0, 10)}
                      {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="photos" className="card-flat p-5 space-y-4">
          {canManage && (
            <UploadButton
              label="+ Adaugă poză"
              accept="image/*"
              onUploaded={onUploadPhoto}
              testId="upload-photo"
            />
          )}
          {(trailer.photo_ids || []).length === 0 ? (
            <div className="text-sm text-zinc-400 italic text-center py-6">Nicio poză.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {(trailer.photo_ids || []).map((pid) => (
                <div key={pid} className="relative group aspect-square bg-zinc-100 rounded overflow-hidden">
                  <AuthImg fileId={pid} className="w-full h-full object-cover"/>
                  {canManage && (
                    <button
                      onClick={() => deletePhoto(pid)}
                      className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600"
                      title="Șterge poză"
                    >
                      <X size={14}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="card-flat p-0 overflow-hidden">
          {history.length === 0 ? (
            <div className="p-6 text-sm text-zinc-400 italic text-center">Niciun eveniment în istoric.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-[var(--border)]">
                <tr>
                  <th className="p-2 text-left text-[10px] font-semibold text-zinc-500 uppercase">Dată</th>
                  <th className="p-2 text-left text-[10px] font-semibold text-zinc-500 uppercase">Acțiune</th>
                  <th className="p-2 text-left text-[10px] font-semibold text-zinc-500 uppercase">Detalii</th>
                  <th className="p-2 text-left text-[10px] font-semibold text-zinc-500 uppercase">Utilizator</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="p-2 text-xs text-zinc-500 tabular-nums">{(h.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2 text-xs font-semibold">{h.action}</td>
                    <td className="p-2 text-xs">{h.message || h.note || "—"}</td>
                    <td className="p-2 text-xs text-zinc-600">{h.actor_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TabsContent>
      </Tabs>

      {editing && (
        <TrailerFormDialog
          trailer={trailer}
          cars={cars}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}

      {linkDialog && (
        <LinkCarDialog
          trailer={trailer}
          cars={cars}
          onClose={() => setLinkDialog(false)}
          onSaved={() => { setLinkDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function Grid({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-[var(--border)] last:border-b-0">
      <div className="col-span-1 text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? "font-mono" : ""} ${!value ? "text-zinc-300 italic" : "text-zinc-800"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function LinkCarDialog({ trailer, cars, onClose, onSaved }) {
  const [carId, setCarId] = useState(trailer.linked_car_id || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/trailers/${trailer.id}/link`, { car_id: carId || null, note: note || null });
      toast.success(carId ? "Remorcă atașată" : "Remorcă detașată");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="link-car-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 size={18}/> Atașare autoturism</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Autoturism</Label>
            <Select value={carId || "__none__"} onValueChange={(v) => setCarId(v === "__none__" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Detașează (fără autoturism) —</SelectItem>
                {cars.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.numar_inmatriculare || c.marca || c.id.slice(0, 6)}
                    {c.marca ? ` · ${c.marca} ${c.model || ""}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full h-9 px-3 border border-[var(--border)] rounded text-sm"
              placeholder="Ex: transfer pentru proiectul X"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="link-confirm">
            <Save size={14} className="mr-1"/>{saving ? "Se salvează…" : "Confirmă"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
